import { StyledIconBase } from "@styled-icons/styled-icon";
import { External } from "assets/icons/Common";
import { Col, IconButtonInverted, Row } from "components/primitives";
import { FC } from "react";
import styled from "styled-components";

import { ScheduleItemData } from "./interfaces";

interface Props {
  isComplete: boolean;
  setIsCompleted: (id: string, completed: boolean) => void;
}

export const ScheduleItem: FC<Props & ScheduleItemData> = ({
  isComplete,
  setIsCompleted,
  id,
  color,
  activity,
  time,
  link,
  image,
}) => {
  const validLink = link ? (link.startsWith("https://") ? link : `https://${link}`) : link;
  const dateTime = time ? new Date(time) : undefined;

  const toggleIsCompleted = (e: any) => {
    e.stopPropagation();
    setIsCompleted(id, !isComplete);
  };

  const openLink = (e: any) => {
    e.stopPropagation();
    window.open(validLink, "_blank");
  };

  return (
    <OuterContainer color={color} isComplete={isComplete} onClick={toggleIsCompleted}>
      <CompletionContainer>
        <Checkbox color={color} checked={isComplete} onChange={toggleIsCompleted} />
      </CompletionContainer>
      <Image>{image}</Image>
      <CopyContainer>
        <p style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
          {activity}
        </p>
        {dateTime && (
          <small>
            {dateTime.toLocaleTimeString("en-us", { hour: "2-digit", minute: "2-digit" })}
          </small>
        )}
      </CopyContainer>
      <ExternalLink>
        {link && (
          <IconButtonInverted
            accentColor={color}
            withShadow={false}
            Icon={External}
            onClick={openLink}
          />
        )}
      </ExternalLink>
    </OuterContainer>
  );
};

const OuterContainer = styled(Row).attrs({ className: "no-drag" })<{
  color: string;
  isComplete: boolean;
}>`
  width: 100%;
  min-height: 20cqw;
  background-color: ${(props) => props.color};
  border-radius: 3px;
  gap: 1cqw;
  padding: 1cqw 0.5cqw;
  align-items: center;
  opacity: ${(props) => (props.isComplete ? "0.6" : "1")};
  transition: opacity 0.5s ease-in;
  min-width: 0;
`;

const CompletionContainer = styled(Row)`
  flex: 0.15;
  justify-content: center;
  align-items: center;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })<{ color: string }>`
  appearance: none;
  width: 8cqw;
  height: 8cqw;
  border: 1cqw solid black;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.05),
    inset 0px -15px 10px -12px rgba(0, 0, 0, 0.05);
  display: grid;
  position: relative;
  border-radius: 50%;

  &:checked {
    background-color: black;
  }

  &:hover {
    filter: brightness(90%);
  }

  &:checked:before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80%;
    height: 80%;
    background-color: ${(props) => props.color};
    transform-origin: bottom left;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
  }
`;

const Image = styled.div`
  flex: 0.15;
  font-size: 8cqw;
  text-align: center;
`;

const CopyContainer = styled(Col)`
  flex: 0.6;
  justify-content: center;
  color: black;
  font-size: 5cqw;
  line-height: 1.33em;
  padding: 0 2cqw;
  min-width: 0;
  overflow: hidden;
  word-break: break-word;
  text-align: left;

  p {
    font-weight: 600;
  }
`;

const ExternalLink = styled(Col)`
  flex: 0.15;
  justify-content: center;
  align-items: center;

  ${StyledIconBase} {
    width: 80%;
    aspect-ratio: 1;
  }
`;
